import { useApolloClient, useLazyQuery, useQuery } from '@apollo/client';
import { useMutation } from '@apollo/client';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid';
import { startOfDay } from 'date-fns';
import format from 'date-fns/format';
import { formatMarketingChannel } from 'lib/attribution-display';
import {
  CAREER_SPONSORSHIP_LEAD_TITLE,
  isCareerSponsorshipLead,
} from 'lib/career-sponsorship-leads';
import { ensureProgrammeMembershipForEvent } from 'lib/ensure-programme-membership-for-event';
import { restartLeadCadence } from 'lib/lead-cadence-persist';
import { getLeadLostReasonLabel } from 'lib/lead-lost-reasons';
import {
  QUOTE_AUTO_INFO_SENT_STATUSES,
  isSalesOpportunity,
  statusAfterConvertToSalesOp,
} from 'lib/lead-sales-opportunity';
import { MENTORSHIP_LEAD_TITLE, isMentorshipLead } from 'lib/mentorship-leads';
import { PATHWAY_LEAD_TITLE, isPathwayLead } from 'lib/pathway-leads';
import { RPL_LEAD_TITLE, isRplLead } from 'lib/rpl-leads';
import { maskContactValue, shouldMaskLeadContact } from 'lib/sales-agent-leads';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';

import AttributionDetails from 'components/leads/attribution-details';
import CareerSponsorshipLeadDetails from 'components/leads/career-sponsorship-lead-details';
import CloseLeadModal from 'components/leads/close-lead-modal';
import LeadActivityPanel from 'components/leads/lead-activity-panel';
// import LeadCadencePanel from 'components/leads/lead-cadence-panel';
import LeadHeaderContact from 'components/leads/lead-header-contact';
import LeadHeaderScheduledCall from 'components/leads/lead-header-scheduled-call';
import LeadHeaderTimestamps from 'components/leads/lead-header-timestamps';
import LeadInterestDetails, {
  getLeadInterestLabel,
} from 'components/leads/lead-interest-details';
import LeadOwnershipControls from 'components/leads/lead-ownership-controls';
import LeadQuotesPanel from 'components/leads/lead-quotes-panel';
import LeadReminderBanner from 'components/leads/lead-reminder-banner';
import LeadSendMessageChoiceModal from 'components/leads/lead-send-message-choice-modal';
import LeadTabNav from 'components/leads/lead-tab-nav';
// import LeadTracksPanel from 'components/leads/lead-tracks-panel';
import MentorshipLeadDetails from 'components/leads/mentorship-lead-details';
import PathwayLeadDetails from 'components/leads/pathway-lead-details';
import RplLeadDetails from 'components/leads/rpl-lead-details';
import { useOpenMissiveEmail } from 'components/leads/use-open-missive-email';
import { useOpenMissiveWhatsApp } from 'components/leads/use-open-missive-whatsapp';
import SendAsset from 'components/molecules/send-asset';
import Message from 'components/organisms/leads/message';

import copyToClipboard from 'utils/clipboard';
import { isSalesAgent } from 'utils/roles';

import { getQuotes } from 'queries/enrollment';
import { getEvents } from 'queries/events';
import {
  insertLeadTouchpointLog,
  upsertLeadCadenceState,
} from 'queries/lead-cadence';
import {
  addLeadContact,
  addLeadConversion,
  closeLead as closeLeadMutation,
  convertLeadToSalesOpportunity,
  getLeadById,
  moveLeadBackToContact,
  reopenLead as reopenLeadMutation,
  updateLeadById,
  updateLeadStatus,
} from 'queries/leads';
import { insertRegistration } from 'queries/registration';

import { useOrganization } from 'providers/organization';
import { useSession } from 'providers/session';
import { useUI } from 'providers/ui';

import AddToEventComponent from './add-to-event';
import Calls from './calls';
import SetReminder from './set-reminder';

// import { getLeads } from "queries/leads";

const Modal = dynamic(() => import('components/molecules/modal'));
const NewEnrollmentModal = dynamic(
  () => import('components/organisms/enrollment/new-enrollment'),
);
const NewQuoteModal = dynamic(
  () => import('components/organisms/enrollment/new-quote'),
);
const NewClientModal = dynamic(
  () => import('components/organisms/client/new-client-modal'),
);

enum ViewTypes {
  'sourceInformation',
  'messages',
  'notes',
  'about',
}

const formatPreferenceText = (value?: string | null) =>
  value ? value.replace(/_/g, ' ') : '';

const buildPreferenceRows = (preferences?: any) => {
  if (!preferences) {
    return [];
  }

  return [
    {
      label: 'Preferred month',
      value: preferences.preferred_month_label || preferences.preferred_month,
    },
    {
      label: 'Preferred schedule',
      value: formatPreferenceText(preferences.preferred_schedule),
    },
    {
      label: 'Start timeframe',
      value: formatPreferenceText(preferences.start_timeframe),
    },
    {
      label: 'Training mode',
      value: formatPreferenceText(preferences.training_mode),
    },
    {
      label: 'Placement',
      value: formatPreferenceText(preferences.placement),
    },
  ].filter((row) => row.value);
};

export default function Leads({
  leadID,
  statusValues = null,
  columns = null,
  onLeadBoardChange = null,
}) {
  const ui = useUI();
  const org = useOrganization();
  const { user, claims } = useSession();
  const apolloClient = useApolloClient();
  const [view, setView] = useState(ViewTypes.sourceInformation);
  const [createRegistration] = useMutation(insertRegistration);
  const [convertedLead] = useMutation(addLeadConversion);
  const [getLeadData, { data: leadData, loading }] = useLazyQuery(getLeadById);
  const [updateStatus] = useMutation(updateLeadStatus);
  const [closeLead, { loading: closingLead }] = useMutation(closeLeadMutation);
  const [reopenLead, { loading: reopeningLead }] =
    useMutation(reopenLeadMutation);
  const [convertToSalesOp, { loading: convertingToSalesOp }] = useMutation(
    convertLeadToSalesOpportunity,
  );
  const [moveBackToContact, { loading: movingBackToContact }] = useMutation(
    moveLeadBackToContact,
  );
  const [addContact] = useMutation(addLeadContact);
  const [updateLead] = useMutation(updateLeadById);
  const [upsertCadenceState] = useMutation(upsertLeadCadenceState);
  const [insertCadenceLog] = useMutation(insertLeadTouchpointLog);
  const [closeLeadModalOpen, setCloseLeadModalOpen] = useState(false);
  const member = user && org.teamMember(claims['x-hasura-user-id']);
  const { openMissiveWhatsApp, WhatsAppComposeModal } =
    useOpenMissiveWhatsApp();
  const { openMissiveEmail, EmailComposeModal } = useOpenMissiveEmail();

  const [state, setState] = useState({
    profile: false,
    actions: false,
    newClient: false,
    call: null,
    addToEventModal: false,
    reminderModal: false,
    assetModal: false,
    newEnrollment: false,
    newQuote: false,
    sendMessageModal: false,
    sendMessageChoiceModal: false,
  });

  const leadUserId = leadData?.lead?.user || null;
  const {
    data: quotesData,
    loading: quotesLoading,
    refetch: refetchQuotes,
  } = useQuery(getQuotes, {
    skip: !leadUserId,
    variables: {
      where: { user_id: { _eq: leadUserId } },
      order_by: { created_at: 'desc' },
    },
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    getLeadData({
      variables: {
        id: leadID.id || leadID,
      },
    });
  }, [leadID, state.newClient, state.addToEventModal, state.reminderModal]);

  useEffect(() => {
    if (
      leadData?.lead &&
      (isRplLead(leadData.lead) || isMentorshipLead(leadData.lead))
    ) {
      setView(ViewTypes.about);
    }
  }, [leadData?.lead?.id]);

  const addToEvent = async (data) => {
    // return
    const parsedData = JSON.parse(data.upcoming_event);
    try {
      await createRegistration({
        variables: {
          data: {
            user_id: leadData.lead.user,
            event_id: parsedData.id,
            status: 'PAID',
            type: 'STUDENT',
            session_id: null,
          },
        },
        refetchQueries: [
          {
            query: getEvents,
            variables: {
              where: {
                start: {
                  _gte: startOfDay(new Date()),
                },
              },
            },
            fetchPolicy: 'network-only',
          },
        ],
      });

      let programmeMessage = '';
      try {
        const programmeLink = await ensureProgrammeMembershipForEvent(
          apolloClient,
          {
            userId: leadData.lead.user,
            eventId: parsedData.id,
            seatType: 'STUDENT',
          },
        );
        if (programmeLink.created) {
          programmeMessage = programmeLink.programmeTitle
            ? ` Also added to programme: ${programmeLink.programmeTitle}.`
            : ' Also added to the linked programme.';
        } else if (programmeLink.updated) {
          programmeMessage = programmeLink.programmeTitle
            ? ` Programme link updated: ${programmeLink.programmeTitle}.`
            : ' Programme training date link updated.';
        }
      } catch (programmeError) {
        console.error('Programme link after event add failed', programmeError);
        programmeMessage =
          ' Registration saved, but linking to the programme failed.';
      }

      await convertedLead({
        variables: {
          data: {
            value: parsedData.price,
            event: parsedData.id,
            lead: leadID,
          },
        },
      });
      setState({
        ...state,
        addToEventModal: false,
      });
      ui.setToast(true, {
        type: 'success',
        title: 'Added to event',
        message: programmeMessage,
      });
    } catch (error) {
      ui.setToast(true, {
        type: 'error',
        title: 'Failed to add to event',
        message: `${error}`,
      });
    }
  };

  const formattedPhone = leadData?.lead?.content?.phone?.replace(/^0/, '+44');
  const leadEmail = leadData?.lead?.content?.email?.trim() || '';
  const contactMasked =
    !!leadData?.lead &&
    shouldMaskLeadContact(leadData.lead, {
      isSalesAgent: isSalesAgent(claims),
      userId: claims['x-hasura-user-id'] ?? '',
    });
  const displayPhone = contactMasked
    ? maskContactValue(leadData?.lead?.content?.phone)
    : leadData?.lead?.content?.phone;
  const displayEmail = contactMasked ? maskContactValue(leadEmail) : leadEmail;
  const displayFormattedPhone = contactMasked ? undefined : formattedPhone;
  const leadRecipientName = [
    leadData?.lead?.content?.name,
    leadData?.lead?.content?.surname,
  ]
    .filter(Boolean)
    .join(' ');

  function handleSendMessage() {
    if (!leadData?.lead) {
      return;
    }

    setState({ ...state, sendMessageChoiceModal: true });
  }

  function handleChooseSendEmail() {
    if (!leadData?.lead || !leadEmail) {
      return;
    }

    setState({
      ...state,
      sendMessageChoiceModal: false,
    });
    openMissiveEmail(leadEmail, {
      leadId: leadData.lead.id,
      name: leadRecipientName || undefined,
    });
  }

  function handleChooseSendSms() {
    if (!leadData?.lead || !formattedPhone) {
      return;
    }

    setState({
      ...state,
      sendMessageChoiceModal: false,
      sendMessageModal: true,
    });
  }

  async function handleWhatsApp() {
    if (!leadData?.lead?.id || !formattedPhone) {
      return;
    }

    const leadName = [
      leadData.lead.content?.name,
      leadData.lead.content?.surname,
    ]
      .filter(Boolean)
      .join(' ');

    openMissiveWhatsApp(formattedPhone, {
      leadId: leadData.lead.id,
      name: leadName || undefined,
    });

    window.setTimeout(() => {
      getLeadData({
        variables: { id: leadData.lead.id },
        fetchPolicy: 'network-only',
      }).catch(() => undefined);
    }, 4000);
  }

  function copyLeadId() {
    const id = leadData?.lead?.id;
    if (!id) {
      return;
    }

    copyToClipboard(id);
    ui.setToast(true, {
      type: 'success',
      title: 'Lead ID copied',
      message: id,
    });
  }

  // A cleaner array to use later on.
  let interestedCourses = null;
  if (leadData?.lead.content.courses) {
    interestedCourses = Object.keys(leadData.lead.content.courses).filter(
      (key) => leadData.lead.content.courses[key],
    );
  }

  const isRplLeadRecord = leadData?.lead ? isRplLead(leadData.lead) : false;
  const isMentorshipLeadRecord = leadData?.lead
    ? isMentorshipLead(leadData.lead)
    : false;
  const isPathwayLeadRecord = leadData?.lead
    ? isPathwayLead(leadData.lead)
    : false;
  const isCareerSponsorshipLeadRecord = leadData?.lead
    ? isCareerSponsorshipLead(leadData.lead)
    : false;

  const tabs = [
    {
      label: 'Source Information',
      type: ViewTypes.sourceInformation,
    },
  ];

  if (
    interestedCourses ||
    leadData?.lead?.content?.quiz ||
    isRplLeadRecord ||
    isMentorshipLeadRecord ||
    isPathwayLeadRecord ||
    isCareerSponsorshipLeadRecord
  ) {
    tabs.push({
      label:
        isCareerSponsorshipLeadRecord &&
        !interestedCourses &&
        !leadData?.lead?.content?.quiz &&
        !isRplLeadRecord &&
        !isMentorshipLeadRecord &&
        !isPathwayLeadRecord
          ? 'Sponsorship details'
          : isPathwayLeadRecord &&
              !interestedCourses &&
              !leadData?.lead?.content?.quiz &&
              !isRplLeadRecord &&
              !isMentorshipLeadRecord
            ? 'Pathway details'
            : isMentorshipLeadRecord &&
                !interestedCourses &&
                !leadData?.lead?.content?.quiz &&
                !isRplLeadRecord &&
                !isPathwayLeadRecord
              ? 'Mentorship estimate'
              : isRplLeadRecord &&
                  !interestedCourses &&
                  !leadData?.lead?.content?.quiz &&
                  !isPathwayLeadRecord
                ? 'RPL eligibility'
                : 'About',
      type: ViewTypes.about,
    });
  }

  const notes = leadData?.lead?.notes;
  const messages = leadData?.lead?.contacts;

  if (loading) {
    return (
      <div className="flex-1 flex justify-center items-center">
        <p>Loading</p>
      </div>
    );
  }

  if (!leadData) {
    return null;
  }

  async function handleCloseLead(payload: {
    lostReason: string;
    lostReasonNotes: string;
  }) {
    if (!leadData?.lead?.id) {
      return;
    }

    try {
      await closeLead({
        variables: {
          id: leadData.lead.id,
          lostReason: payload.lostReason,
          lostReasonNotes: payload.lostReasonNotes || null,
          lostReasonAt: new Date().toISOString(),
        },
      });
      setCloseLeadModalOpen(false);
      await getLeadData({
        variables: { id: leadData.lead.id },
        fetchPolicy: 'network-only',
      });
      ui.setToast(true, {
        type: 'success',
        title: 'Lead closed',
        message: 'Lost reason recorded.',
      });
    } catch (error) {
      ui.setToast(true, {
        type: 'error',
        title: 'Failed to close lead',
        message: `${error}`,
      });
    }
  }

  async function handleOpenLead() {
    if (!leadData?.lead?.id) {
      return;
    }

    try {
      await reopenLead({
        variables: { id: leadData.lead.id },
      });
      await restartLeadCadence({
        lead: leadData.lead,
        upsertState: upsertCadenceState,
        updateLead,
      });
      await getLeadData({ variables: { id: leadData.lead.id } });
      ui.setToast(true, {
        type: 'success',
        title: 'Lead reopened',
        message: '90-day cadence restarted from today.',
      });
    } catch (error) {
      ui.setToast(true, {
        type: 'error',
        title: 'Failed to reopen lead',
        message: `${error}`,
      });
    }
  }

  const changeStatus = async (status: string) => {
    if (status === 'CLOSED') {
      setCloseLeadModalOpen(true);
      return;
    }

    try {
      await updateStatus({
        variables: { id: leadData.lead.id, status: status },
      });

      ui.setToast(true, {
        type: 'success',
        title:
          'Moved from ' +
          columns[leadData.lead.status]?.label +
          ' to ' +
          columns[status]?.label,
        message: '',
      });
    } catch (error) {
      ui.setToast(true, {
        type: 'error',
        title: 'Failed to change status',
        message: `${error}`,
      });
    }
  };

  let leadType = 'Lead';

  if (leadData.lead.metadata && leadData.lead.metadata.title) {
    if (leadData.lead.metadata.title === 'Payment Plans Application')
      leadType = leadData.lead.metadata.title;
    if (leadData.lead.metadata.title === 'Contact Submission')
      leadType = leadData.lead.metadata.title;
    if (leadData.lead.metadata.title === 'Brochure Request')
      leadType = leadData.lead.metadata.title;
    if (leadData.lead.metadata.title === 'Course Preferred Date Request')
      leadType = leadData.lead.metadata.title;
    if (leadData.lead.metadata.title === RPL_LEAD_TITLE)
      leadType = leadData.lead.metadata.title;
    if (leadData.lead.metadata.title === MENTORSHIP_LEAD_TITLE)
      leadType = leadData.lead.metadata.title;
    if (leadData.lead.metadata.title === PATHWAY_LEAD_TITLE)
      leadType = leadData.lead.metadata.title;
    if (leadData.lead.metadata.title === CAREER_SPONSORSHIP_LEAD_TITLE)
      leadType = leadData.lead.metadata.title;
  }

  const isCourseDateInterestLead =
    leadData.lead.metadata?.title === 'Course Preferred Date Request';
  const courseDatePreferences = leadData.lead.metadata?.preferences || {};
  const courseDatePreferenceRows = buildPreferenceRows(courseDatePreferences);
  const courseDateSourcePage =
    courseDatePreferences.source_page || leadData.lead.metadata?.url || null;
  const courseDateCourseUrl =
    courseDatePreferences.course_url ||
    leadData.lead.metadata?.courses?.[0]?.url ||
    null;
  const courseDateVisibleDates =
    courseDatePreferences.available_date_labels || [];
  const leadInterestLabel = getLeadInterestLabel(leadData.lead.metadata);

  const refetchLead = () =>
    getLeadData({
      variables: { id: leadData.lead.id },
      fetchPolicy: 'network-only',
    });

  async function handleConvertToSalesOp() {
    if (!leadData?.lead?.id) {
      return;
    }

    try {
      const nextStatus = statusAfterConvertToSalesOp(leadData.lead.status);
      await convertToSalesOp({
        variables: {
          id: leadData.lead.id,
          salesOpportunityAt: new Date().toISOString(),
          status: nextStatus,
        },
      });
      await refetchLead();
      if (onLeadBoardChange) {
        await onLeadBoardChange();
      }
      ui.setToast(true, {
        type: 'success',
        title: 'Converted to sales opportunity',
        message: 'This lead now appears on the Sales board.',
      });
    } catch (error) {
      ui.setToast(true, {
        type: 'error',
        title: 'Failed to convert lead',
        message: `${error}`,
      });
    }
  }

  async function handleMoveBackToContact() {
    if (!leadData?.lead?.id) {
      return;
    }

    try {
      await moveBackToContact({
        variables: { id: leadData.lead.id },
      });
      await refetchLead();
      if (onLeadBoardChange) {
        await onLeadBoardChange();
      }
      ui.setToast(true, {
        type: 'success',
        title: 'Moved back to Contact',
        message: 'This lead is back on the Contact board.',
      });
    } catch (error) {
      ui.setToast(true, {
        type: 'error',
        title: 'Failed to move lead',
        message: `${error}`,
      });
    }
  }

  async function handleResendQuote(quoteId: string) {
    try {
      await fetch('/api/inngest/trigger', {
        method: 'POST',
        body: JSON.stringify({
          name: 'quote.new',
          data: { id: quoteId },
        }),
      });
      ui.setToast(true, {
        type: 'success',
        title: 'Quote resent',
        message: 'Successfully resent the quote.',
      });
    } catch (error) {
      ui.setToast(true, {
        type: 'error',
        title: 'Error resending quote',
        message: `${error}`,
      });
    }
  }

  async function handleQuoteCreated() {
    setState((current) => ({ ...current, newQuote: false }));
    if (
      leadData?.lead?.status &&
      QUOTE_AUTO_INFO_SENT_STATUSES.has(leadData.lead.status)
    ) {
      try {
        await updateStatus({
          variables: { id: leadData.lead.id, status: 'INFO_SENT' },
        });
      } catch (error) {
        console.warn('Failed to move lead to Info sent after quote', error);
      }
    }
    await refetchLead();
    await refetchQuotes();
    if (onLeadBoardChange) {
      await onLeadBoardChange();
    }
  }

  const isSalesOp = isSalesOpportunity(leadData.lead);

  const leadSubtitle = [
    leadData?.lead?.user ? 'Registered' : 'Unregistered',
    formatMarketingChannel(leadData?.lead?.metadata?.marketing_channel),
  ]
    .filter(Boolean)
    .join(' · ');

  const hasScheduledCall =
    leadData?.lead?.data && Object.keys(leadData.lead.data).length > 0;

  const leadContent = leadData?.lead?.content;
  const hasQualificationContent = Boolean(
    leadContent &&
    (leadContent.isQualifiedMedicalProfessional != null ||
      leadContent.medicalProfession != null ||
      (leadContent.medicalProfessionCustom != null &&
        leadContent.medicalProfessionCustom !== '') ||
      leadContent.hasBeautyQualificationsOrExperience != null),
  );

  return (
    <div className="bg-white h-full oveflow-y-auto" id={leadData.lead.id}>
      <main className="mb-16 w-full px-4 py-4">
        <div className="w-full overflow-visible rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0 flex-1 space-y-2">
                <h1 className="flex items-center gap-2 text-lg font-semibold capitalize leading-snug text-gray-900 sm:text-xl">
                  <span>
                    {leadData.lead.content.name} {leadData.lead.content.surname}
                  </span>
                  {leadData.lead.user ? (
                    <button
                      type="button"
                      onClick={() => ui.setClient(true, leadData.lead.user)}
                      className="inline-flex text-gray-400 hover:text-teal-700"
                      title="Open student overview"
                      aria-label="Open student overview"
                    >
                      <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                    </button>
                  ) : null}
                </h1>

                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  {leadSubtitle ? (
                    <span className="text-xs text-gray-500">
                      {leadSubtitle}
                    </span>
                  ) : null}
                  {leadSubtitle &&
                  (leadData.lead.metadata.title === 'Contact Submission' ||
                    isCourseDateInterestLead ||
                    isRplLeadRecord ||
                    isMentorshipLeadRecord ||
                    isPathwayLeadRecord ||
                    isCareerSponsorshipLeadRecord ||
                    leadData.lead.metadata.ad_set_name) ? (
                    <span className="text-gray-300" aria-hidden="true">
                      ·
                    </span>
                  ) : null}
                  {leadData.lead.metadata.title === 'Contact Submission' && (
                    <span className="rounded-full bg-purple-50 px-2 py-0.5 text-[11px] font-medium text-purple-700 ring-1 ring-purple-100">
                      Contact form
                    </span>
                  )}
                  {isCourseDateInterestLead && (
                    <span className="rounded-full bg-purple-50 px-2 py-0.5 text-[11px] font-medium text-purple-700 ring-1 ring-purple-100">
                      Preferred training date
                    </span>
                  )}
                  {isRplLeadRecord && (
                    <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-700 ring-1 ring-brand-100">
                      {RPL_LEAD_TITLE}
                    </span>
                  )}
                  {isMentorshipLeadRecord && (
                    <span className="rounded-full bg-teal-50 px-2 py-0.5 text-[11px] font-medium text-teal-700 ring-1 ring-teal-100">
                      {MENTORSHIP_LEAD_TITLE}
                    </span>
                  )}
                  {isPathwayLeadRecord && (
                    <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700 ring-1 ring-indigo-100">
                      {PATHWAY_LEAD_TITLE}
                    </span>
                  )}
                  {isCareerSponsorshipLeadRecord && (
                    <span className="rounded-full bg-teal-50 px-2 py-0.5 text-[11px] font-medium text-teal-700 ring-1 ring-teal-100">
                      {CAREER_SPONSORSHIP_LEAD_TITLE}
                    </span>
                  )}
                  {leadInterestLabel ? (
                    <span
                      className="max-w-full truncate rounded-full bg-teal-50 px-2 py-0.5 text-[11px] font-medium text-teal-700 ring-1 ring-teal-100"
                      title={leadInterestLabel}
                    >
                      {leadInterestLabel}
                    </span>
                  ) : null}
                  {leadData.lead.metadata.ad_set_name ? (
                    <span className="rounded-full bg-gray-50 px-2 py-0.5 text-[11px] font-medium text-gray-700 ring-1 ring-gray-200">
                      Social media lead
                    </span>
                  ) : null}
                </div>

                <LeadHeaderTimestamps
                  createdAt={leadData.lead.createdAt}
                  updatedAt={leadData.lead.updatedAt}
                />

                <LeadOwnershipControls
                  leadId={leadData.lead.id}
                  who={leadData.lead.who}
                  claimedAt={leadData.lead.claimed_at}
                  ownerName={[
                    leadData.lead.userByWho?.given_name,
                    leadData.lead.userByWho?.family_name,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onChanged={() =>
                    getLeadData({ variables: { id: leadData.lead.id } })
                  }
                />

                {(leadData.lead.due || hasScheduledCall) && (
                  <div className="flex flex-col gap-1.5 sm:flex-row sm:flex-wrap">
                    {leadData.lead.due ? (
                      <LeadReminderBanner
                        due={leadData.lead.due}
                        variant="chip"
                      />
                    ) : null}
                    {hasScheduledCall ? (
                      <LeadHeaderScheduledCall
                        data={leadData.lead.data}
                        variant="chip"
                      />
                    ) : null}
                  </div>
                )}
              </div>

              <div className="flex flex-shrink-0 flex-row flex-wrap items-center justify-end gap-2">
                {!isSalesOp ? (
                  <button
                    type="button"
                    onClick={handleConvertToSalesOp}
                    disabled={convertingToSalesOp}
                    className="btn-secondary !w-auto whitespace-nowrap border-teal-600 px-4 py-2 text-sm text-teal-800 hover:bg-teal-50"
                  >
                    Convert to sales op
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleMoveBackToContact}
                    disabled={movingBackToContact}
                    className="btn-secondary !w-auto whitespace-nowrap px-4 py-2 text-sm"
                  >
                    Move back to Contact
                  </button>
                )}
                {leadData.lead.user ? (
                  <button
                    onClick={() => setState({ ...state, newEnrollment: true })}
                    type="button"
                    className="btn-primary !w-auto whitespace-nowrap px-4 py-2 text-sm"
                  >
                    Create Enrollment
                  </button>
                ) : (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setState({ ...state, actions: false, newClient: true });
                    }}
                    type="button"
                    className="btn-primary !w-auto whitespace-nowrap px-4 py-2 text-sm"
                  >
                    Register
                  </button>
                )}
                {leadData.lead.user ? (
                  <button
                    type="button"
                    onClick={() => setState({ ...state, newQuote: true })}
                    className="btn-secondary !w-auto whitespace-nowrap px-4 py-2 text-sm"
                  >
                    Send quote
                  </button>
                ) : null}
                {member ? (
                  <button
                    type="button"
                    onClick={handleSendMessage}
                    className="btn-secondary !w-auto whitespace-nowrap px-4 py-2 text-sm"
                  >
                    Send message
                  </button>
                ) : null}
                <select
                  name="status"
                  className="w-auto min-w-[8rem] appearance-none rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                  value={leadData.lead.status}
                  onChange={(e) => changeStatus(e.target.value)}
                >
                  {statusValues &&
                    statusValues.status.map((status) => (
                      <option value={status.value} key={status.value}>
                        {columns[status.value]?.label || status.value}
                      </option>
                    ))}
                </select>
                {leadData.lead.status === 'CLOSED' && (
                  <button
                    onClick={handleOpenLead}
                    type="button"
                    disabled={reopeningLead}
                    className="btn-secondary !w-auto whitespace-nowrap px-4 py-2 text-sm"
                  >
                    Open lead
                  </button>
                )}
              </div>
            </div>

            <LeadHeaderContact
              variant="sidebar"
              leadId={leadData.lead.id}
              content={
                contactMasked
                  ? {
                      ...leadData.lead.content,
                      phone: displayPhone,
                      email: displayEmail,
                    }
                  : leadData.lead.content
              }
              phone={displayPhone}
              email={displayEmail}
              formattedPhone={displayFormattedPhone}
              onUpdated={refetchLead}
              onCall={(phone) => {
                if (contactMasked) return;
                ui.setCall(true, {
                  to: phone,
                  leadId: leadData.lead.id,
                });
              }}
              onWhatsApp={contactMasked ? () => undefined : handleWhatsApp}
            />
            {contactMasked ? (
              <p className="mt-2 text-xs text-amber-700">
                Claim this lead to reveal contact details and start outreach.
              </p>
            ) : null}
          </div>
        </div>

        <div className="mt-4 grid w-full grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            {/* <LeadTracksPanel
              leadId={leadData.lead.id}
              onUpdated={refetchLead}
            /> */}

            {isSalesOp || leadData.lead.user ? (
              <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900">
                      Quotes
                    </h2>
                    <p className="text-xs text-gray-500">
                      Create and track enrollment quotes for this opportunity.
                    </p>
                  </div>
                  {leadData.lead.user ? (
                    <button
                      type="button"
                      onClick={() => setState({ ...state, newQuote: true })}
                      className="btn-secondary !w-auto px-3 py-1.5 text-xs"
                    >
                      Send quote
                    </button>
                  ) : null}
                </div>
                <LeadQuotesPanel
                  quotes={quotesData?.quotes || []}
                  loading={quotesLoading}
                  onResend={handleResendQuote}
                />
              </section>
            ) : null}

            {/* <LeadCadencePanel
              lead={leadData.lead}
              trackAssignments={leadData.lead.track_assignments || []}
              onUpdated={refetchLead}
              onCall={() => {
                ui.setCall(true, {
                  to: formattedPhone,
                  leadId: leadData.lead.id,
                });
              }}
              onCloseLead={() => setCloseLeadModalOpen(true)}
            /> */}

            <LeadTabNav tabs={tabs} activeType={view} onChange={setView} />
            {view === ViewTypes.about && (
              <div id="about">
                <section aria-labelledby="about-title">
                  <div className="bg-white border border-gray-300 sm:rounded">
                    <div className="px-4 py-5 sm:px-6">
                      <h2
                        id="about-title"
                        className="text-lg font-bold text-gray-900"
                      >
                        {isRplLeadRecord
                          ? 'RPL eligibility'
                          : isMentorshipLeadRecord
                            ? 'Mentorship estimate'
                            : isPathwayLeadRecord
                              ? 'Training pathway'
                              : isCareerSponsorshipLeadRecord
                                ? 'Career sponsorship'
                                : 'About'}
                      </h2>
                    </div>
                    <div className="border-t border-gray-300 px-4 py-5 sm:px-6">
                      <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                        {isRplLeadRecord ? (
                          <RplLeadDetails lead={leadData.lead} />
                        ) : null}
                        {isMentorshipLeadRecord ? (
                          <MentorshipLeadDetails lead={leadData.lead} />
                        ) : null}
                        {isPathwayLeadRecord ? (
                          <PathwayLeadDetails lead={leadData.lead} />
                        ) : null}
                        {isCareerSponsorshipLeadRecord ? (
                          <CareerSponsorshipLeadDetails lead={leadData.lead} />
                        ) : null}
                        {isCourseDateInterestLead && (
                          <div className="sm:col-span-2">
                            <dt className="text-sm font-medium text-gray-500 mb-4">
                              Preferred training date
                            </dt>
                            <dd className="mt-1">
                              <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                  {leadData.lead.metadata?.courses?.[0]
                                    ?.title && (
                                    <div>
                                      <div className="text-xs font-semibold uppercase tracking-wide text-purple-700">
                                        Course
                                      </div>
                                      <div className="mt-1 text-sm font-semibold text-gray-900">
                                        {
                                          leadData.lead.metadata.courses[0]
                                            .title
                                        }
                                      </div>
                                    </div>
                                  )}
                                  {courseDatePreferenceRows.map((row) => (
                                    <div key={row.label}>
                                      <div className="text-xs font-semibold uppercase tracking-wide text-purple-700">
                                        {row.label}
                                      </div>
                                      <div className="mt-1 text-sm text-gray-900 capitalize">
                                        {row.value}
                                      </div>
                                    </div>
                                  ))}
                                  {courseDateVisibleDates.length > 0 && (
                                    <div className="sm:col-span-2">
                                      <div className="text-xs font-semibold uppercase tracking-wide text-purple-700">
                                        Visible dates on page
                                      </div>
                                      <ul className="mt-2 space-y-1 text-sm text-gray-700">
                                        {courseDateVisibleDates.map((date) => (
                                          <li key={date}>{date}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  {leadData.lead.content.message && (
                                    <div className="sm:col-span-2">
                                      <div className="text-xs font-semibold uppercase tracking-wide text-purple-700">
                                        Learner note
                                      </div>
                                      <div className="mt-1 text-sm text-gray-700">
                                        {leadData.lead.content.message}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </dd>
                          </div>
                        )}
                        {hasQualificationContent ? (
                          <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500 mb-4">
                              Qualifications
                            </dt>
                            <dd className="mt-1 text-xs text-gray-700 flex flex-col gap-5">
                              {leadData.lead.content
                                .isQualifiedMedicalProfessional !== null &&
                                leadData.lead.content
                                  .isQualifiedMedicalProfessional !==
                                  undefined && (
                                  <div>
                                    <div className="font-semibold mb-1">
                                      Qualified medical professional:
                                    </div>
                                    {leadData.lead.content
                                      .isQualifiedMedicalProfessional
                                      ? 'Yes'
                                      : 'No'}
                                  </div>
                                )}
                              {leadData.lead.content.medicalProfession !==
                                null &&
                                leadData.lead.content.medicalProfession !==
                                  undefined && (
                                  <div>
                                    <div className="font-semibold mb-1">
                                      Medical profession:
                                    </div>
                                    <span className="capitalize">
                                      {leadData.lead.content.medicalProfession}
                                    </span>
                                  </div>
                                )}
                              {leadData.lead.content.medicalProfessionCustom !==
                                null &&
                                leadData.lead.content
                                  .medicalProfessionCustom !== undefined &&
                                leadData.lead.content
                                  .medicalProfessionCustom !== '' && (
                                  <div>
                                    <div className="font-semibold mb-1">
                                      Medical experience:
                                    </div>
                                    {
                                      leadData.lead.content
                                        .medicalProfessionCustom
                                    }
                                  </div>
                                )}
                              {leadData.lead.content
                                .hasBeautyQualificationsOrExperience !== null &&
                                leadData.lead.content
                                  .hasBeautyQualificationsOrExperience !==
                                  undefined && (
                                  <div>
                                    <div className="font-semibold mb-1">
                                      Beauty qualifications or experience:
                                    </div>
                                    <span className="capitalize">
                                      {leadData.lead.content
                                        .hasBeautyQualificationsOrExperience
                                        ? 'Yes'
                                        : 'No'}
                                    </span>
                                  </div>
                                )}
                            </dd>
                          </div>
                        ) : null}

                        {leadData.lead.content.quiz ? (
                          <div className="sm:col-span-2">
                            <dt className="text-sm font-medium text-gray-500 mb-4">
                              Quiz Results
                            </dt>
                            <dd className="mt-1 text-xs text-gray-700">
                              {typeof leadData.lead.content.quiz ===
                              'string' ? (
                                // Fallback for string format
                                <div>{leadData.lead.content.quiz}</div>
                              ) : (
                                // Structured quiz data
                                <div className="space-y-4">
                                  {/* Quiz Result */}
                                  {leadData.lead.content.quiz.result && (
                                    <div className="bg-purple-50 p-3 rounded-md">
                                      <div className="font-semibold text-purple-800 mb-1">
                                        Recommended Pathway:
                                      </div>
                                      <div className="text-purple-700 capitalize">
                                        {leadData.lead.content.quiz.result.replace(
                                          /-/g,
                                          ' ',
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Questions and Answers */}
                                  {leadData.lead.content.quiz.questions && (
                                    <div className="space-y-3">
                                      <div className="font-semibold text-gray-800 mb-2">
                                        Quiz Questions:
                                      </div>
                                      {leadData.lead.content.quiz.questions.map(
                                        (question, index) => (
                                          <div
                                            key={index}
                                            className="border-l-2 border-purple-200 pl-3"
                                          >
                                            <div className="font-medium text-gray-700 mb-1">
                                              {question.question}
                                            </div>
                                            <div className="text-purple-600 capitalize">
                                              {question.answer}
                                            </div>
                                          </div>
                                        ),
                                      )}
                                    </div>
                                  )}

                                  {/* Voucher Code */}
                                  {leadData.lead.content.quiz.voucherCode && (
                                    <div className="bg-green-50 p-3 rounded-md">
                                      <div className="font-semibold text-green-800 mb-1">
                                        Voucher Code:
                                      </div>
                                      <div className="text-green-700 font-mono">
                                        {leadData.lead.content.quiz.voucherCode}
                                      </div>
                                    </div>
                                  )}

                                  {/* Recommended Courses */}
                                  {leadData.lead.content.quiz
                                    .recommendedCourses && (
                                    <div className="bg-blue-50 p-3 rounded-md">
                                      <div className="font-semibold text-blue-800 mb-2">
                                        Recommended Courses:
                                      </div>
                                      <ul className="space-y-1">
                                        {leadData.lead.content.quiz.recommendedCourses.map(
                                          (course, index) => (
                                            <li
                                              key={index}
                                              className="text-blue-700 flex items-center"
                                            >
                                              <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                                              {course}
                                            </li>
                                          ),
                                        )}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              )}
                            </dd>
                          </div>
                        ) : null}

                        {interestedCourses && (
                          <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500 mb-4">
                              Interested courses
                            </dt>
                            <dd className="mt-1 text-xs text-gray-700">
                              <ul className="pl-5">
                                {interestedCourses.map((course: string) => {
                                  // Convert course_name to Course Name.
                                  const prettyName = course.replace(/_/g, ' ');

                                  return (
                                    <li
                                      key={course}
                                      className="relative capitalize after:bg-purple-600 after:rounded-full after:absolute after:-left-4 after:top-1/2 after:w-1.5 after:h-1.5 after:-translate-y-1/2"
                                    >
                                      {prettyName}
                                    </li>
                                  );
                                })}
                              </ul>
                            </dd>
                          </div>
                        )}
                      </dl>
                    </div>
                  </div>
                </section>
              </div>
            )}
            {view === ViewTypes.sourceInformation && (
              <div id="source-info">
                <section aria-labelledby="source-info-title">
                  <div className="bg-white border border-gray-300 sm:rounded">
                    <div className="px-4 py-5 sm:px-6">
                      <h2
                        id="source-info-title"
                        className="text-lg font-bold text-gray-900"
                      >
                        Source Information
                      </h2>
                    </div>
                    <div className="border-t border-gray-300 px-4 py-5 sm:px-6">
                      <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                        <LeadInterestDetails
                          metadata={leadData.lead.metadata}
                        />
                        {isCareerSponsorshipLeadRecord ? (
                          <CareerSponsorshipLeadDetails lead={leadData.lead} />
                        ) : null}
                        <AttributionDetails
                          data={leadData.lead.metadata}
                          leadSource={leadData.lead.source}
                        />
                        {leadData.lead.metadata.adgroup && (
                          <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">
                              Ad Group ID
                            </dt>
                            <dd className="mt-1 text-xs text-gray-700">
                              {leadData.lead.metadata.adgroup}
                            </dd>
                          </div>
                        )}
                        {leadData.lead.metadata.adgroup_name && (
                          <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">
                              Ad Group Name
                            </dt>
                            <dd className="mt-1 text-xs text-gray-700">
                              {leadData.lead.metadata.adgroup_name}
                            </dd>
                          </div>
                        )}
                        {leadData.lead.metadata.campaign && (
                          <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">
                              Campaign
                            </dt>
                            <dd className="mt-1 text-xs text-gray-700">
                              {leadData.lead.metadata.campaign}
                            </dd>
                          </div>
                        )}
                        <div className="sm:col-span-1">
                          <dt className="text-sm font-medium text-gray-500">
                            ID
                          </dt>
                          <dd className="mt-1 flex items-start gap-2 text-xs text-gray-700">
                            <span className="break-all font-mono">
                              {leadData.lead.id}
                            </span>
                            <button
                              type="button"
                              onClick={copyLeadId}
                              className="shrink-0 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                            >
                              Copy
                            </button>
                          </dd>
                        </div>
                        {leadData.lead.metadata.device && (
                          <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">
                              Device
                            </dt>
                            <dd className="mt-1 text-xs text-gray-700">
                              {leadData.lead.metadata.device}
                            </dd>
                          </div>
                        )}
                        {leadData.lead.metadata.keyword && (
                          <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">
                              Keyword
                            </dt>
                            <dd className="mt-1 text-xs text-gray-700">
                              {leadData.lead.metadata.keyword}
                            </dd>
                          </div>
                        )}
                        <div className="sm:col-span-1">
                          <dt className="text-sm font-medium text-gray-500">
                            Page Title
                          </dt>
                          <dd className="mt-1 text-xs text-gray-700">
                            {leadData.lead.metadata.title}
                          </dd>
                        </div>
                        {leadData.lead.metadata.url && (
                          <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">
                              Page Url
                            </dt>
                            <a
                              className="underline text-purple-700 text-xs"
                              target="_blank"
                              rel="noreferrer"
                              href={
                                leadData.lead.metadata.url.startsWith('http')
                                  ? leadData.lead.metadata.url
                                  : `https://cosmetic.college${leadData.lead.metadata.url}`
                              }
                            >
                              <dd className="text-xs text-gray-700 mt-1 break-all">
                                {leadData.lead.metadata.url.startsWith('http')
                                  ? leadData.lead.metadata.url
                                  : `cosmetic.college${leadData.lead.metadata.url}`}
                              </dd>
                            </a>
                          </div>
                        )}
                        {isCourseDateInterestLead && courseDateCourseUrl && (
                          <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">
                              Course page
                            </dt>
                            <a
                              className="underline text-purple-700 text-xs"
                              target="_blank"
                              rel="noreferrer"
                              href={`https://cosmetic.college${courseDateCourseUrl}`}
                            >
                              <dd className="text-xs text-gray-700 mt-1">
                                {`cosmetic.college${courseDateCourseUrl}`}
                              </dd>
                            </a>
                          </div>
                        )}
                        {isCourseDateInterestLead && courseDateSourcePage && (
                          <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">
                              Source page
                            </dt>
                            <a
                              className="underline text-purple-700 text-xs"
                              target="_blank"
                              rel="noreferrer"
                              href={`https://cosmetic.college${courseDateSourcePage}`}
                            >
                              <dd className="text-xs text-gray-700 mt-1">
                                {`cosmetic.college${courseDateSourcePage}`}
                              </dd>
                            </a>
                          </div>
                        )}
                        {leadData.lead.metadata.courses && (
                          <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">
                              Courses
                            </dt>
                            <dd className="mt-1 text-xs text-gray-700">
                              {leadData.lead.metadata.courses.map((x, i) => (
                                <span key={`${x.title}-${i}`}>{x.title}</span>
                              ))}
                            </dd>
                          </div>
                        )}
                        {isCourseDateInterestLead &&
                          courseDatePreferenceRows.map((row) => (
                            <div className="sm:col-span-1" key={row.label}>
                              <dt className="text-sm font-medium text-gray-500">
                                {row.label}
                              </dt>
                              <dd className="mt-1 text-xs text-gray-700 capitalize">
                                {row.value}
                              </dd>
                            </div>
                          ))}
                        {leadData.lead.metadata.utm_campaign && (
                          <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">
                              utm_campaign
                            </dt>
                            <dd className="mt-1 text-xs text-gray-700">
                              {leadData.lead.metadata.utm_campaign}
                            </dd>
                          </div>
                        )}
                        {leadData.lead.metadata.utm_medium && (
                          <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">
                              utm_medium
                            </dt>
                            <dd className="mt-1 text-xs text-gray-700">
                              {leadData.lead.metadata.utm_medium}
                            </dd>
                          </div>
                        )}
                        {leadData.lead.metadata.utm_term && (
                          <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">
                              utm_term
                            </dt>
                            <dd className="mt-1 text-xs text-gray-700">
                              {leadData.lead.metadata.utm_term}
                            </dd>
                          </div>
                        )}
                        {leadData.lead.metadata.gclid && (
                          <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">
                              gclid
                            </dt>
                            <dd className="mt-1 text-xs text-gray-700 break-all">
                              {leadData.lead.metadata.gclid}
                            </dd>
                          </div>
                        )}
                        {leadData.lead.metadata.wbraid && (
                          <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">
                              wbraid
                            </dt>
                            <dd className="mt-1 text-xs text-gray-700 break-all">
                              {leadData.lead.metadata.wbraid}
                            </dd>
                          </div>
                        )}
                        {leadData.lead.metadata.gbraid && (
                          <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">
                              gbraid
                            </dt>
                            <dd className="mt-1 text-xs text-gray-700 break-all">
                              {leadData.lead.metadata.gbraid}
                            </dd>
                          </div>
                        )}
                        {leadData.lead.metadata.ad_id && (
                          <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">
                              ad_id
                            </dt>
                            <dd className="mt-1 text-xs text-gray-700 break-all">
                              {leadData.lead.metadata.ad_id}
                            </dd>
                          </div>
                        )}
                        {leadData.lead.metadata.ad_name && (
                          <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">
                              ad_name
                            </dt>
                            <dd className="mt-1 text-xs text-gray-700 break-all">
                              {leadData.lead.metadata.ad_name}
                            </dd>
                          </div>
                        )}
                        {leadData.lead.metadata.ad_set_id && (
                          <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">
                              ad_set_id
                            </dt>
                            <dd className="mt-1 text-xs text-gray-700 break-all">
                              {leadData.lead.metadata.ad_set_id}
                            </dd>
                          </div>
                        )}
                        {leadData.lead.metadata.ad_set_name && (
                          <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">
                              ad_set_name
                            </dt>
                            <dd className="mt-1 text-xs text-gray-700 break-all">
                              {leadData.lead.metadata.ad_set_name}
                            </dd>
                          </div>
                        )}
                        {leadData.lead.metadata.campaign_id && (
                          <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">
                              campaign_id
                            </dt>
                            <dd className="mt-1 text-xs text-gray-700 break-all">
                              {leadData.lead.metadata.campaign_id}
                            </dd>
                          </div>
                        )}
                        {leadData.lead.metadata.campaign_name && (
                          <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">
                              campaign_name
                            </dt>
                            <dd className="mt-1 text-xs text-gray-700 break-all">
                              {leadData.lead.metadata.campaign_name}
                            </dd>
                          </div>
                        )}
                      </dl>
                    </div>
                  </div>
                </section>
              </div>
            )}
            {leadData.lead.status === 'CLOSED' ? (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Lost reason
                </p>
                {leadData.lead.lost_reason ? (
                  <>
                    <p className="mt-1 text-sm font-medium text-gray-900">
                      {getLeadLostReasonLabel(leadData.lead.lost_reason)}
                    </p>
                    {leadData.lead.lost_reason_notes ? (
                      <p className="mt-2 text-sm text-gray-600">
                        {leadData.lead.lost_reason_notes}
                      </p>
                    ) : null}
                    {leadData.lead.lost_reason_at ? (
                      <p className="mt-2 text-xs text-gray-500">
                        Recorded{' '}
                        {format(
                          new Date(leadData.lead.lost_reason_at),
                          'do MMM yyyy p',
                        )}
                      </p>
                    ) : null}
                  </>
                ) : (
                  <>
                    <p className="mt-1 text-sm text-amber-700">
                      No reason recorded yet.
                    </p>
                    <button
                      type="button"
                      onClick={() => setCloseLeadModalOpen(true)}
                      className="mt-2 text-sm font-semibold text-purple-700 hover:text-purple-600"
                    >
                      Add lost reason
                    </button>
                  </>
                )}
              </div>
            ) : null}

            {leadType !== 'Lead' ? (
              <div className="space-y-2 rounded-xl border border-gray-200 bg-white p-4">
                <h2 className="text-md font-bold text-gray-900">{leadType}</h2>
                {isCareerSponsorshipLeadRecord ? (
                  <CareerSponsorshipLeadDetails lead={leadData.lead} />
                ) : isPathwayLeadRecord ? (
                  <PathwayLeadDetails lead={leadData.lead} />
                ) : isMentorshipLeadRecord ? (
                  <MentorshipLeadDetails lead={leadData.lead} />
                ) : (
                  leadData.lead.metadata.courses &&
                  leadData.lead.metadata.courses.map((course) => (
                    <>
                      {course.date ? (
                        <Link
                          href={`/events/${course.date}`}
                          className="flex items-center text-purple-600 hover:text-purple-700"
                        >
                          {course.title}
                          <ArrowTopRightOnSquareIcon width={16} />
                        </Link>
                      ) : (
                        <p className="flex items-center text-purple-600">
                          {course.title}
                        </p>
                      )}
                    </>
                  ))
                )}
                {isCourseDateInterestLead && (
                  <div className="rounded-lg border border-purple-200 bg-purple-50 p-3 text-sm text-gray-800">
                    <div className="text-xs font-semibold uppercase tracking-wide text-purple-700">
                      Preferred training date
                    </div>
                    <div className="mt-3 space-y-2">
                      {courseDatePreferenceRows.map((row) => (
                        <div key={row.label} className="flex flex-col">
                          <span className="text-xs font-medium text-gray-500">
                            {row.label}
                          </span>
                          <span className="capitalize">{row.value}</span>
                        </div>
                      ))}
                      {courseDateCourseUrl && (
                        <a
                          href={`https://cosmetic.college${courseDateCourseUrl}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center text-purple-700 hover:text-purple-800"
                        >
                          Open course page
                          <ArrowTopRightOnSquareIcon width={16} />
                        </a>
                      )}
                      {courseDateSourcePage && (
                        <a
                          href={`https://cosmetic.college${courseDateSourcePage}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center text-purple-700 hover:text-purple-800"
                        >
                          Open source page
                          <ArrowTopRightOnSquareIcon width={16} />
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>

          <aside className="flex h-[min(28rem,calc(100dvh-12rem))] min-h-0 flex-col overflow-hidden lg:col-span-1 lg:sticky lg:top-4 lg:h-[calc(100dvh-5rem)] lg:max-h-[calc(100dvh-5rem)] lg:self-start">
            {member ? (
              <LeadActivityPanel
                notes={notes}
                messages={messages}
                org={org}
                member={member}
                leadId={leadData.lead.id}
                email={leadData.lead.content?.email}
                phone={leadData.lead.content?.phone}
                client={leadData.lead.content}
                userId={leadData.lead.user}
                onOpenCall={(callId) =>
                  setState((s) => ({ ...s, call: callId }))
                }
                quickActions={{
                  onViewProfile: () => {
                    ui.setClient(true, leadData.lead.user);
                  },
                  onReminder: () => setState({ ...state, reminderModal: true }),
                  reminderLabel: leadData.lead.due
                    ? 'Update reminder'
                    : 'Set reminder',
                  onSendAsset: () => setState({ ...state, assetModal: true }),
                }}
              />
            ) : null}
          </aside>
        </div>
        {state.sendMessageChoiceModal ? (
          <LeadSendMessageChoiceModal
            open={state.sendMessageChoiceModal}
            email={leadEmail}
            phone={leadData?.lead?.content?.phone}
            onClose={() =>
              setState({ ...state, sendMessageChoiceModal: false })
            }
            onChooseEmail={handleChooseSendEmail}
            onChooseSms={handleChooseSendSms}
          />
        ) : null}
        {state.sendMessageModal && member ? (
          <Modal
            open={state.sendMessageModal}
            large
            overflow="overflow-visible"
            title="Send SMS"
            subTitle={
              formattedPhone
                ? `Text message to ${formattedPhone}`
                : 'Text message'
            }
            close={() => setState({ ...state, sendMessageModal: false })}
          >
            <Message
              org={org}
              member={member}
              messages={messages.filter((x) => x.type !== 'call')}
              lead={leadData.lead.id}
              client={leadData.lead.content}
              type="lead"
              showHistory={false}
              initialView="text"
              composeChannels={['text']}
            />
          </Modal>
        ) : null}
        {state.newClient && (
          <Modal
            open={state.newClient}
            close={() => setState({ ...state, newClient: false })}
          >
            <NewClientModal
              prefill={{
                name: leadData.lead.content.name,
                surname: leadData.lead.content.surname,
                email: leadData.lead.content.email,
                phone: leadData.lead.content.phone,
              }}
              onComplete={() => {
                setState({
                  ...state,
                  newClient: false,
                });
              }}
            />
          </Modal>
        )}
        {state.addToEventModal && (
          <Modal
            open={state.addToEventModal}
            large={true}
            close={() => setState({ ...state, addToEventModal: false })}
          >
            <AddToEventComponent submit={addToEvent} />
          </Modal>
        )}
        {state.reminderModal && (
          <Modal
            open={state.reminderModal}
            large={true}
            close={() => setState({ ...state, reminderModal: false })}
          >
            <SetReminder
              close={() => setState({ ...state, reminderModal: false })}
              type="lead"
              item={leadData}
            />
          </Modal>
        )}
        {state.newEnrollment && (
          <Modal
            xlarge
            open={state.newEnrollment}
            close={() => setState({ ...state, newEnrollment: false })}
          >
            <NewEnrollmentModal
              prefill={leadData.lead}
              setStates={setState}
              metadata={leadData.lead.metadata}
              onComplete={() => {
                setState({
                  ...state,
                  newEnrollment: false,
                });
              }}
            />
          </Modal>
        )}
        {state.newQuote && (
          <Modal
            xlarge
            open={state.newQuote}
            close={() => setState({ ...state, newQuote: false })}
          >
            <NewQuoteModal
              prefill={{
                user: leadData.lead.user,
                lead_id: leadData.lead.id,
                content: leadData.lead.content,
                name: leadData.lead.content?.name,
                surname: leadData.lead.content?.surname,
                email: leadData.lead.content?.email,
                phone: leadData.lead.content?.phone,
              }}
              metadata={leadData.lead.metadata}
              onComplete={handleQuoteCreated}
            />
          </Modal>
        )}
        {state.assetModal && (
          <Modal
            open={state.assetModal}
            large={true}
            close={() => setState({ ...state, assetModal: false })}
          >
            <SendAsset
              lead={leadData && leadData.lead}
              close={() => setState({ ...state, assetModal: false })}
            />
          </Modal>
        )}

        <Modal
          full
          open={!!state.call}
          close={() => setState((s) => ({ ...s, call: null }))}
        >
          <div className="flex flex-col flex-1 w-full h-full">
            {state.call && (
              <Calls
                callID={state.call}
                statusValues={statusValues}
                columns={columns}
              />
            )}
          </div>
        </Modal>
      </main>

      <CloseLeadModal
        open={closeLeadModalOpen}
        loading={closingLead}
        leadName={
          leadData?.lead?.content?.name
            ? `${leadData.lead.content.name}${
                leadData.lead.content.surname
                  ? ` ${leadData.lead.content.surname}`
                  : ''
              }`
            : null
        }
        onClose={() => {
          setCloseLeadModalOpen(false);
        }}
        onConfirm={handleCloseLead}
      />
      {WhatsAppComposeModal}
      {EmailComposeModal}
    </div>
  );
}
